import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PencilIcon, RocketIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PerformerCardFace } from '../../components/swipe/PerformerCardFace';
import { KeysEditor } from '../../components/performer/KeysEditor';
import { AvailabilityCalendar } from '../../components/performer/AvailabilityCalendar';
import { PerformerPreferencesForm } from '../../components/performer/PerformerPreferencesForm';
import { AppearanceSetting } from '../../components/AppearanceSetting';
import { SettingsSection } from '../../components/SettingsSection';
import { ProSheet } from '../../components/ProSheet';
import { Button } from '../../components/ui/Button';
import { NumberStepper } from '../../components/ui/NumberStepper';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { travelCost } from '../../utils/event';
import { baht } from '../../utils/format';
import { LanguageSwitcher } from '../../components/ui/LanguageSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';
export function Profile() {
  const {
    me,
    updateMe,
    performerPrefs,
    setPerformerPrefs,
    isPro,
    offers,
    matches,
    getEvent
  } = useMarketplace();
  const {
    hash
  } = useLocation();
  const {
    t
  } = useLanguage();
  const [editingBio, setEditingBio] = useState(false);
  const [bio, setBio] = useState(me.bio);
  const [proOpen, setProOpen] = useState(false);
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [hash]);
  const bookedDates = offers.filter(o => o.status === 'accepted').map(o => matches.find(m => m.id === o.matchId)).filter(m => m?.performerId === me.id).map(m => getEvent(m!.eventId)?.date).filter((d): d is string => Boolean(d));
  return <div className="mx-auto w-full max-w-6xl px-5 pb-12 pt-6 lg:px-8 lg:pt-8" data-id="element-1258" data-mp="3ros2dbx6dso7">
      <h1 className="text-[28px] font-bold tracking-[-0.015em]" data-id="element-1259" data-mp="ejhst1odycg">Profile</h1>
      <div className="mt-6 grid gap-10 lg:grid-cols-[360px_minmax(0,1fr)]" data-id="element-1260" data-mp="1wig6rp1y0mfe">
        <aside className="lg:sticky lg:top-8 lg:self-start" data-id="element-1261" data-mp="1rkxv3qwjjt3p">
          <p className="mb-3 text-sm font-bold text-ink-3" data-id="element-1262" data-mp="1rmir7kyi7k3o">How organisers see you</p>
          <div className="relative mx-auto aspect-[4/5] w-full max-w-[360px]" data-id="element-1263" data-mp="25je9m40nanwe">
            <PerformerCardFace performer={me} fit={{
            score: 94,
            reason: 'Plays soul & r&b · fits your budget'
          }} travel={travelCost(me, 7)} data-id="element-1264" data-mp="21jx2l65klip4" />
          </div>

          {isPro ? <div className="mt-6 rounded-md bg-surface-2 p-5" data-id="element-1265" data-mp="18upwnm3sdqtu">
              <p className="font-bold" data-id="element-1266" data-mp="1mlbf59pbvyr7">Pro insights · last 30 days</p>
              <dl className="mt-3 grid grid-cols-3 gap-3" data-id="element-1267" data-mp="1mmmix9irx6ib">
                {[['Views', '312'], ['Saves', '41'], ['Plays', '128']].map(([k, v]) => <div key={k} data-id="element-1268" data-mp="u2o2mioeds1q">
                    <dt className="text-[13px] text-ink-3" data-id="element-1269" data-mp="q48hylfsgvrh">{k}</dt>
                    <dd className="tabular text-xl font-bold" data-id="element-1270" data-mp="fziay15j31ge">{v}</dd>
                  </div>)}
              </dl>
              <Button variant="primary" size="sm" className="mt-4" icon={<RocketIcon className="h-4 w-4" data-id="element-1272" data-mp="ksbc6f1sfrnq" />} onClick={() => toast.success('Boost live for 24 hours')} data-id="element-1271" data-mp="kqgt76a2u9ma">
                Use weekly boost
              </Button>
            </div> : <div className="mt-6 rounded-md bg-ink p-5 text-bg" data-id="element-1273" data-mp="ayb4q6j1aabu">
              <p className="text-lg font-bold" data-id="element-1274" data-mp="yrb2rs8v2gmj">Encorza Pro</p>
              <p className="mt-1 text-sm opacity-80" data-id="element-1275" data-mp="13ot34u8tbnix">See who liked you, apply without limits and boost your profile weekly.</p>
              <Button variant="accent" size="sm" className="mt-4" onClick={() => setProOpen(true)} data-id="element-1276" data-mp="oqsduj2fw9xr">
                Try Pro · ฿299/mo
              </Button>
            </div>}
        </aside>

        <div className="min-w-0" data-id="element-1277" data-mp="39gzsu3sxeu3n">
          <SettingsSection title="About" action={!editingBio && <Button variant="ghost" size="sm" icon={<PencilIcon className="h-4 w-4" data-id="element-1280" data-mp="3uoilewkvrug6" />} onClick={() => setEditingBio(true)} data-id="element-1279" data-mp="3eern34h1msb0">
                  Edit
                </Button>} data-id="element-1278" data-mp="39k5k9pp1282j">
            {editingBio ? <div data-id="element-1281" data-mp="3kzimfd6doz1z">
                <textarea value={bio} onChange={e => setBio(e.target.value)} rows={4} aria-label="Bio" className="w-full resize-none rounded-sm border border-line bg-surface px-4 py-3 text-[16px] leading-relaxed focus:border-ink focus:outline-none" data-id="element-1282" data-mp="3kyz2jqx640lb" />
                <div className="mt-3 flex gap-2" data-id="element-1283" data-mp="7j1foevn7arr">
                  <Button variant="ghost" size="sm" onClick={() => {
                setBio(me.bio);
                setEditingBio(false);
              }} data-id="element-1284" data-mp="7hq25nceaong">
                    Cancel
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => {
                updateMe({
                  bio
                });
                setEditingBio(false);
                toast.success('Bio updated');
              }} data-id="element-1285" data-mp="2sauvp22v1hh1">
                    Save
                  </Button>
                </div>
              </div> : <p className="max-w-[65ch] text-[17px] leading-relaxed text-ink-2" data-id="element-1286" data-mp="12qtmfslnu1u4">{me.bio}</p>}
          </SettingsSection>

          <SettingsSection title="Keys" description="Songs you can perform. Add samples so organisers can hear you." data-id="element-1287" data-mp="1cqkgxua80gu7">
            <KeysEditor keys={me.keys} onChange={keys => updateMe({
            keys
          })} data-id="element-1288" data-mp="1hicbefwhz6pm" />
          </SettingsSection>

          <SettingsSection title="Pricing & travel" description="Organisers see your fee plus an automatic travel estimate." data-id="element-1289" data-mp="1mg40l25rceq4">
            <div className="max-w-lg space-y-5" data-id="element-1290" data-mp="1qk3f5f7h93gu">
              <NumberStepper label="Standard fee" value={me.price} step={500} min={1000} onChange={price => updateMe({
              price
            })} format={baht} hint="Per performance" data-id="element-1291" data-mp="1vidbht4xua41" />
              <NumberStepper label="Travel radius" value={me.travelRadiusKm} step={10} min={10} max={500} onChange={travelRadiusKm => updateMe({
              travelRadiusKm
            })} format={v => `${v} km`} data-id="element-1292" data-mp="13kfvu421jc3" />
              <NumberStepper label="Travel cost" value={me.travelCostPerKm} step={1} min={0} max={50} onChange={travelCostPerKm => updateMe({
              travelCostPerKm
            })} format={v => `฿${v}/km`} hint="Charged on the round trip" data-id="element-1293" data-mp="3u73tytol2zca" />
            </div>
          </SettingsSection>

          <SettingsSection title="Availability" description="Booked gigs block out automatically." data-id="element-1294" data-mp="h020x9xmlmdu">
            <div className="max-w-sm" data-id="element-1295" data-mp="czi4k8kvmmsr">
              <AvailabilityCalendar bookedDates={bookedDates} data-id="element-1296" data-mp="cygrjykavq8e" />
            </div>
          </SettingsSection>

          <SettingsSection id="preferences" title="Matching preferences" description="These shape your fit scores and what shows up in Discover." data-id="element-1297" data-mp="qpljeehffe86">
            <PerformerPreferencesForm value={performerPrefs} onChange={setPerformerPrefs} data-id="element-1298" data-mp="10o1c2lw3qvjs" />
          </SettingsSection>

          <SettingsSection title="Reviews" description={`${me.rating.toFixed(1)} average from ${me.reviewCount} reviews`} data-id="element-1299" data-mp="vqj00mmsg07k">
            <ul className="divide-y divide-line" data-id="element-1300" data-mp="1fbrq526kwdcn">
              {me.reviews.map(r => <li key={r.author} className="py-3" data-id="element-1301" data-mp="1jcbjrvry0yu1">
                  <p className="text-[16px] leading-relaxed" data-id="element-1302" data-mp="15dc43dmerua0">“{r.text}”</p>
                  <p className="mt-1 text-sm font-semibold text-ink-3" data-id="element-1303" data-mp="15fg77tchgp98">{r.author}</p>
                </li>)}
            </ul>
          </SettingsSection>

          <SettingsSection title="Appearance" data-id="element-1304" data-mp="vmha1n2x7qe0">
            <AppearanceSetting data-id="element-1305" data-mp="10dzbsdcrpv01" />
          </SettingsSection>

          <SettingsSection title={t('settings.language')} description={t('settings.languageHint')} data-id="element-1306" data-mp="10fac23708kme">
            <LanguageSwitcher id="profile-lang" data-id="element-1307" data-mp="mofcjhiebfvh" />
          </SettingsSection>
        </div>
      </div>
      <ProSheet open={proOpen} onClose={() => setProOpen(false)} data-id="element-1308" data-mp="rlx9e9j524n4" />
    </div>;
}